import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Users, IndianRupee, Mountain } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingFlow = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(2);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProperty();
  }, [propertyId]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/properties/${propertyId}`);
      setProperty(response.data);
    } catch (error) {
      toast.error('Failed to fetch property details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const calculateNights = () => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const nights = calculateNights();
  const totalAmount = nights * (property?.price_per_night || 0);

  const handleBooking = async () => {
    if (!checkIn || !checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    if (nights <= 0) {
      toast.error('Check-out date must be after check-in date');
      return;
    }

    try {
      setSubmitting(true);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/bookings`,
        {
          property_id: propertyId,
          check_in: checkIn,
          check_out: checkOut,
          guests: parseInt(guests),
          total_amount: totalAmount
        },
        { withCredentials: true }
      );

      const bookingId = response.data.booking_id;

      const orderResponse = await axios.post(
        `${BACKEND_URL}/api/payments/create-order?booking_id=${bookingId}`,
        {},
        { withCredentials: true }
      );

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || '',
        amount: orderResponse.data.amount,
        currency: 'INR',
        order_id: orderResponse.data.id,
        name: 'GoHimalayan',
        description: `Booking for ${property.title}`,
        handler: async (paymentResponse) => {
          try {
            await axios.post(
              `${BACKEND_URL}/api/payments/verify`,
              {
                booking_id: bookingId,
                payment_id: paymentResponse.razorpay_payment_id,
                order_id: paymentResponse.razorpay_order_id,
                signature: paymentResponse.razorpay_signature
              },
              { withCredentials: true }
            );

            toast.success('Booking confirmed!');
            navigate('/traveler-dashboard');
          } catch (error) {
            toast.error('Payment verification failed');
            console.error(error);
          }
        },
        theme: {
          color: '#8B2E2E'
        }
      };

      if (window.Razorpay) {
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        toast.info('Payment gateway not available. Booking created as pending.');
        navigate('/traveler-dashboard');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to make a booking');
        navigate('/login');
      } else {
        toast.error('Failed to create booking');
      }
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-spinner">
        <div className="w-16 h-16 border-4 border-[#8B2E2E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div 
              className="flex items-center gap-2 cursor-pointer" 
              onClick={() => navigate('/')}
              data-testid="nav-logo"
            >
              <Mountain className="h-8 w-8 text-[#8B2E2E]" />
              <h1 className="text-2xl font-bold text-[#8B2E2E]" style={{fontFamily: 'Playfair Display, serif'}}>GoHimalayan</h1>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 
          className="text-3xl font-bold mb-8 text-[#4A4A4A]"
          style={{fontFamily: 'Playfair Display, serif'}}
          data-testid="booking-title"
        >
          Complete Your Booking
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-8" data-testid="booking-form">
              <h2 className="text-xl font-bold mb-6" style={{fontFamily: 'Playfair Display, serif'}}>Booking Details</h2>
              
              <div className="space-y-6">
                <div>
                  <Label htmlFor="checkIn" className="text-[#4A4A4A] mb-2 block">Check-in Date</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#4A4A4A]/50" />
                    <Input 
                      id="checkIn"
                      type="date"
                      value={checkIn}
                      onChange={(e) => setCheckIn(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="pl-10 h-12 rounded-xl"
                      data-testid="check-in-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="checkOut" className="text-[#4A4A4A] mb-2 block">Check-out Date</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#4A4A4A]/50" />
                    <Input 
                      id="checkOut"
                      type="date"
                      value={checkOut}
                      onChange={(e) => setCheckOut(e.target.value)}
                      min={checkIn || new Date().toISOString().split('T')[0]}
                      className="pl-10 h-12 rounded-xl"
                      data-testid="check-out-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="guests" className="text-[#4A4A4A] mb-2 block">Number of Guests</Label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#4A4A4A]/50" />
                    <Input 
                      id="guests"
                      type="number"
                      min="1"
                      max={property?.max_guests || 10}
                      value={guests}
                      onChange={(e) => setGuests(e.target.value)}
                      className="pl-10 h-12 rounded-xl"
                      data-testid="guests-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-8 shadow-lg sticky top-8" data-testid="booking-summary">
              <h2 className="text-xl font-bold mb-4" style={{fontFamily: 'Playfair Display, serif'}}>Summary</h2>
              
              {property && (
                <div className="space-y-4 mb-6">
                  <div className="flex gap-3">
                    <img 
                      src={property.images[0] || 'https://images.pexels.com/photos/31790464/pexels-photo-31790464.jpeg'}
                      alt={property.title}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div>
                      <h3 className="font-semibold text-[#4A4A4A]">{property.title}</h3>
                      <p className="text-sm text-[#4A4A4A]/70">{property.location.city}</p>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4 space-y-2">
                    <div className="flex justify-between text-[#4A4A4A]">
                      <span>Price per night</span>
                      <span className="flex items-center">
                        <IndianRupee className="h-4 w-4" />
                        {property.price_per_night.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between text-[#4A4A4A]">
                      <span>Nights</span>
                      <span>{nights}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg text-[#4A4A4A] pt-2 border-t border-gray-200">
                      <span>Total</span>
                      <span className="flex items-center">
                        <IndianRupee className="h-5 w-5" />
                        {totalAmount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <Button 
                className="w-full rounded-full bg-[#8B2E2E] hover:bg-[#6B1E1E] text-white py-6 text-lg font-medium btn-primary"
                onClick={handleBooking}
                disabled={submitting || !checkIn || !checkOut || nights <= 0}
                data-testid="confirm-booking-btn"
              >
                {submitting ? 'Processing...' : 'Confirm Booking'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingFlow;
